// src/components/separation/terminationlist.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, AlertTriangle, Minus } from 'lucide-react';
import { TerminationRecord } from '../../types/separation';
import { getTerminations } from '../../api/separation';
import TerminationModal from './terminationmodal';
import { terminationTypeBadge, employeeLabel, fmtDate } from './uiHelpers';

interface TerminationListProps {
  onViewChecklist?: (type: string, id: string) => void;
  onCreateSettlement?: (type: string, id: string, employeeId: string) => void;
  userRole?: string;
  canTerminate?: boolean;
  onChanged?: () => void;
}

const TerminationList: React.FC<TerminationListProps> = ({
  onViewChecklist,
  onCreateSettlement,
  userRole = 'employee',
  canTerminate = false,
  onChanged,
}) => {
  const [terminations, setTerminations] = useState<TerminationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isTerminateModalOpen, setIsTerminateModalOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string>('');
  const limit = 20;

  const loadTerminations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTerminations({ page, limit });
      setTerminations(response.data || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error('Failed to load terminations:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadTerminations();
  }, [loadTerminations]);

  const totalPages = Math.ceil(total / limit) || 1;

  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <>
      {canTerminate && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => {
              setSelectedEmployeeId('');
              setSelectedEmployeeName('');
              setIsTerminateModalOpen(true);
            }}
            className="gt-btn is-sm bg-red-600 text-white hover:bg-red-700"
          >
            <Minus size={16} /> Terminate Employee
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Termination Records</h3>
          <p className="text-xs text-gray-500 mt-0.5">Total: {total}</p>
        </div>

        <div className="overflow-auto max-h-[70vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-gray-500">
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Employee</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Termination Date</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Last Working Day</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Type</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Rehire</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {terminations.map((t) => {
                const badge = terminationTypeBadge(t.termination_type);
                return (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{employeeLabel(t)}</div>
                      <div className="text-xs text-gray-400">
                        {t.employee_code || `#${t.id.slice(0, 8)}`}
                        {t.designation ? ` · ${t.designation}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{fmtDate(t.termination_date)}</td>
                    <td className="px-6 py-4 text-gray-600">{fmtDate(t.last_working_day)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-6 py-4">
                      {t.eligible_for_rehire ? (
                        <span className="text-emerald-600 font-medium">Yes</span>
                      ) : (
                        <span className="text-red-600 font-medium">No</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap space-x-3">
                      <button onClick={() => onViewChecklist?.('TERMINATION', t.id)} className="text-blue-600 hover:text-blue-800">
                        Checklist
                      </button>
                      <button
                        onClick={() => onCreateSettlement?.('TERMINATION', t.id, t.employee_id)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        Settlement
                      </button>
                    </td>
                  </tr>
                );
              })}
              {terminations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <AlertTriangle className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    No termination records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="gt-btn is-ghost is-sm disabled:opacity-50">
              <ChevronLeft size={14} /> Previous
            </button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="gt-btn is-ghost is-sm disabled:opacity-50">
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      <TerminationModal
        isOpen={isTerminateModalOpen}
        onClose={() => {
          setIsTerminateModalOpen(false);
          setSelectedEmployeeId('');
          setSelectedEmployeeName('');
        }}
        employeeId={selectedEmployeeId}
        employeeName={selectedEmployeeName}
        onSuccess={() => {
          loadTerminations();
          onChanged?.();
        }}
      />
    </>
  );
};

export default TerminationList;
